'use client'

import { useEffect, useState } from 'react'

import { useConversationContext } from '@marketplace-web/domain/core-conversation'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import {
  ConversationActionButton,
  ConversationActionProps,
} from '@marketplace-web/domain/conversation-action'

import { clickEvent } from '_libs/common/event-tracker/events'
import { ShipmentDeliveryType } from 'constants/shipment'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { getShipmentDetails } from 'data/api/get-shipment-details'
import { updateDeliveryOptionChangeRequest } from 'data/api/update-delivery-option-change-request'
import { ResponseCode } from 'data/api/response-codes'

import GenericErrorModal from '../GenericErrorModal'
import AcceptChangeDeliveryOptionRequestModal from './AcceptChangeDeliveryOptionRequestModal'
import { DeliveryOptionChangeRequestStatus } from '../../constants'

const AcceptChangeDeliveryOptionRequest = ({
  title,
  primary,
  size,
  theme,
  inverse,
}: ConversationActionProps) => {
  const { track } = useTracking()

  const { conversation } = useConversationContext()

  const transactionId = conversation?.transaction?.id
  const shipmentId = conversation?.transaction?.shipment?.id
  const shippingOrderId = conversation?.transaction?.shippingOrderId

  const { fetch: fetchShipmentDetails, data: shipmentDetails } = useFetch(getShipmentDetails)

  const [isChangeDeliveryOptionRequestModalOpen, setIsChangeDeliveryOptionRequestModalOpen] =
    useState(false)
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [dataLoadingError, setDataLoadingError] = useState(false)

  useEffect(() => {
    if (!shipmentId) return
    if (shipmentDetails) return

    fetchShipmentDetails(shipmentId)
  }, [shipmentId, shipmentDetails, fetchShipmentDetails])

  const handleSubmit = async () => {
    setIsDataLoading(true)

    if (!shipmentId) return

    const response = await updateDeliveryOptionChangeRequest({
      shipmentId,
      status: DeliveryOptionChangeRequestStatus.Accepted,
    })

    if ('errors' in response) {
      setIsDataLoading(false)
      setDataLoadingError(true)

      return
    }

    if (response.code === ResponseCode.Ok) {
      setIsDataLoading(false)
    }
  }

  const trackButtonClick = () => {
    if (!transactionId) return

    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: ClickableElement.AcceptCarrierChangeRequest,
        screen: Screen.MessageReply,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const handleClick = () => {
    trackButtonClick()

    if (shipmentDetails?.delivery_type === ShipmentDeliveryType.Home) {
      handleSubmit()

      return
    }

    setIsChangeDeliveryOptionRequestModalOpen(true)
  }

  const handlChangeDeliveryOptionRequestModalClose = () =>
    setIsChangeDeliveryOptionRequestModalOpen(false)

  const handleErrorModalClose = () => setDataLoadingError(false)

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        onClick={handleClick}
        testId="select-new-pickup-point"
        isLoading={isDataLoading}
      />
      <AcceptChangeDeliveryOptionRequestModal
        shipmentId={shipmentId}
        shippingOrderId={shippingOrderId}
        isOpen={isChangeDeliveryOptionRequestModalOpen}
        onClose={handlChangeDeliveryOptionRequestModalClose}
      />
      <GenericErrorModal isShown={dataLoadingError} onClose={handleErrorModalClose} />
    </>
  )
}

export default AcceptChangeDeliveryOptionRequest
